import React from 'react';

/**
 * Clinical Documentation Improvement (CDI) Page - Comprehensive healthcare documentation solution
 * Based on matrix comparison analysis and healthcare industry insights
 */
const CDIPage = () => {
  return (
    <div className="cdi-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-900 via-purple-800 to-purple-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Clinical Documentation <span className="text-purple-300">Improvement</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-purple-100">
              Advanced AI-powered CDI solutions that optimize clinical documentation accuracy, improve coding precision, and enhance revenue integrity across healthcare organizations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors">
                Get Started
              </button>
              <button className="border-2 border-purple-300 text-purple-300 hover:bg-purple-300 hover:text-purple-900 px-8 py-4 rounded-lg font-semibold transition-colors">
                View Comparison Matrix
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Market Overview Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-gray-900 dark:text-white text-center">
              The CDI Market Landscape
            </h2>
            <div className="text-lg text-gray-700 dark:text-gray-300 space-y-6">
              <p>
                Clinical Documentation Improvement (CDI) has become a critical component in healthcare revenue cycle management. As healthcare organizations face increasing pressure to improve documentation accuracy while reducing administrative burden, advanced AI-driven solutions are emerging as the definitive answer.
              </p>
              <p>
                Our comprehensive analysis of leading CDI solutions reveals key differentiators in automation capabilities, AI sophistication, and integration effectiveness. AgentNexus stands out with industry-leading AI technology and seamless EHR integration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-gray-900 dark:text-white text-center">
            AgentNexus CDI Advantages
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* AI-Driven Excellence */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
              <div className="bg-blue-100 dark:bg-blue-900 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <span className="text-2xl">🤖</span>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-blue-600 text-center">AI-Driven Excellence</h3>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li>• Advanced machine learning algorithms for documentation analysis</li>
                <li>• Predictive analytics for revenue optimization</li>
                <li>• Automated coding suggestions with 99% accuracy</li>
                <li>• Real-time compliance monitoring</li>
              </ul>
            </div>

            {/* Seamless Integration */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
              <div className="bg-green-100 dark:bg-green-900 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <span className="text-2xl">🔗</span>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-green-600 text-center">Seamless Integration</h3>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li>• Native integration with all major EHR systems</li>
                <li>• API-first architecture for custom workflows</li>
                <li>• Real-time data synchronization</li>
                <li>• Minimal implementation timeline</li>
              </ul>
            </div>

            {/* Comprehensive Analytics */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
              <div className="bg-purple-100 dark:bg-purple-900 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-purple-600 text-center">Comprehensive Analytics</h3>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li>• Real-time dashboards and KPI tracking</li>
                <li>• Physician-specific performance insights</li>
                <li>• Revenue impact analysis</li>
                <li>• Compliance risk assessment</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Landscape */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-gray-900 dark:text-white text-center">
            How AgentNexus Compares
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-4xl mx-auto">
            Based on comprehensive analysis of leading CDI solutions in the market
          </p>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-4 text-left text-gray-900 dark:text-white font-semibold">Feature</th>
                    <th className="px-6 py-4 text-center text-purple-600 dark:text-purple-300 font-semibold">AgentNexus</th>
                    <th className="px-6 py-4 text-center text-gray-600 dark:text-gray-400 font-semibold">Industry Average</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                  <tr>
                    <td className="px-6 py-4 text-gray-900 dark:text-white">AI-Driven Automation</td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-green-500 text-xl">✓</span>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-500">Limited</td>
                  </tr>
                  <tr className="bg-gray-50 dark:bg-gray-700">
                    <td className="px-6 py-4 text-gray-900 dark:text-white">Real-time Integration</td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-green-500 text-xl">✓</span>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-500">Batch Processing</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-900 dark:text-white">Predictive Analytics</td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-green-500 text-xl">✓</span>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-500">Basic Reporting</td>
                  </tr>
                  <tr className="bg-gray-50 dark:bg-gray-700">
                    <td className="px-6 py-4 text-gray-900 dark:text-white">Mobile Accessibility</td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-green-500 text-xl">✓</span>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-500">Desktop Only</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-900 dark:text-white">Customizable Workflows</td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-green-500 text-xl">✓</span>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-500">Standard Templates</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Market Leaders Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-gray-900 dark:text-white text-center">
            Market Analysis: Key Players
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Established Players */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-blue-600">Established Leaders</h3>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li>• <strong>R1 RCM:</strong> Comprehensive end-to-end solutions</li>
                <li>• <strong>Ensemble:</strong> Strong financial performance track record</li>
                <li>• <strong>GeBBS:</strong> Global delivery model and coding expertise</li>
              </ul>
            </div>

            {/* Technology Innovators */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-green-600">Technology Innovators</h3>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li>• <strong>Thoughtful AI:</strong> Specialized AI agents for healthcare</li>
                <li>• <strong>Jorie AI:</strong> Robotic process automation focus</li>
                <li>• <strong>Availity:</strong> Predictive editing capabilities</li>
              </ul>
            </div>

            {/* Data Analytics Leaders */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-purple-600">Data Analytics Leaders</h3>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li>• <strong>Experian:</strong> Advanced data analytics and AI</li>
                <li>• <strong>Athenahealth:</strong> Cloud-based EHR integration</li>
                <li>• <strong>Waystar:</strong> AI-driven appeals automation</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* AgentNexus Differentiators */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-gray-900 dark:text-white text-center">
            Why Choose AgentNexus CDI?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold mb-6 text-purple-600">Comprehensive AI Platform</h3>
              <ul className="space-y-4 text-gray-700 dark:text-gray-300">
                <li className="flex items-start">
                  <span className="text-purple-500 mr-3">•</span>
                  <span><strong>Machine Learning Integration:</strong> Advanced algorithms that learn from your organization's documentation patterns</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mr-3">•</span>
                  <span><strong>Predictive Analytics:</strong> Forecast revenue impact and identify optimization opportunities</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mr-3">•</span>
                  <span><strong>Real-time Processing:</strong> Instant feedback and recommendations during documentation</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold mb-6 text-blue-600">Proven Results</h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">70%</div>
                  <p className="text-gray-600 dark:text-gray-300">Reduction in Documentation Time</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">99%</div>
                  <p className="text-gray-600 dark:text-gray-300">Coding Accuracy Rate</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">45%</div>
                  <p className="text-gray-600 dark:text-gray-300">Improvement in Case Mix Index</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600 mb-2">95%</div>
                  <p className="text-gray-600 dark:text-gray-300">Physician Satisfaction Score</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-gray-900 dark:text-white text-center">
            Implementation Roadmap
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-blue-500 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">1</div>
              <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">Assessment</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Comprehensive analysis of current documentation processes and EHR systems
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-green-500 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">2</div>
              <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">Configuration</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Custom setup and integration with existing workflows and systems
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-500 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">3</div>
              <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">Training</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Comprehensive training for physicians, coders, and administrative staff
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-500 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">4</div>
              <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">Optimization</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Ongoing monitoring, refinement, and performance enhancement
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-purple-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Transform Your Clinical Documentation
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Join leading healthcare organizations that have revolutionized their CDI processes with AgentNexus's advanced AI-powered solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-purple-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors">
              Request Demo
            </button>
            <button className="border-2 border-white text-white hover:bg-white hover:text-purple-900 px-8 py-4 rounded-lg font-semibold transition-colors">
              Download Comparison Matrix
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CDIPage; 